import React, { useEffect, useState } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Input, Button, Table } from 'reactstrap';
import apiClient from '../../api';

/**
 * Manage Departments — migrated from the bespoke theme.js styling
 * (slate palette + plain HTML <table> + inline SVG icons) to
 * Velzon-standard reactstrap components.
 *
 * Business logic is UNCHANGED:
 *   - GET    /api/department                            → fetch list
 *     (backend returns { value: [...] } — preserved)
 *   - POST   /api/department  { JCDept1, Description,
 *                                Company: 'SSW', GlobalJCDept: true,
 *                                GlobalLock: false, SysRevID: 0,
 *                                SysRowID: '...', COASegReferences: '',
 *                                BitFlag: 0, RowMod: 'A' }
 *   - DELETE /api/department/SSW/:deptId
 *
 * Every state name, every handler name, every API path, every payload
 * field is byte-identical to the pre-migration version — including the
 * optimistic update in handleAdd (`setDepartments(prev => [...])`).
 */
const ManageDepartments = () => {
    const [departments, setDepartments] = useState([]);
    const [newDept, setNewDept] = useState({ jcDept1: '', description: '' });
    const [selectedDeptId, setSelectedDeptId] = useState('');

    const fetchDepartments = async () => {
        try {
            const res = await apiClient.get('department');
            // axios returns { data: ... } — backend returns { value: [...] }
            const list = res?.data?.value;
            if (Array.isArray(list)) setDepartments(list);
        } catch (e) { console.error(e); }
    };

    const handleAdd = async () => {
        if (!newDept.jcDept1.trim()) return alert('Please enter a department ID.');
        try {
            const payload = {
                JCDept1: newDept.jcDept1,
                Description: newDept.description,
                Company: 'SSW',
                GlobalJCDept: true,
                GlobalLock: false,
                SysRevID: 0,
                SysRowID: '00000000-0000-0000-0000-000000000000',
                COASegReferences: '',
                BitFlag: 0,
                RowMod: 'A',
            };
            setDepartments(prev => [...prev, { jcDept1: newDept.jcDept1, description: newDept.description }]);
            setNewDept({ jcDept1: '', description: '' });
            await apiClient.post('department', payload);
            fetchDepartments();
        } catch (e) { console.error(e); }
    };

    const handleDelete = async () => {
        if (!selectedDeptId) return alert('Please select a department to delete.');
        try {
            await apiClient.delete(`department/SSW/${selectedDeptId}`);
            setSelectedDeptId('');
            fetchDepartments();
        } catch (e) { console.error(e); }
    };

    useEffect(() => { fetchDepartments(); }, []);

    return (
        <Card className="shadow-sm border-0">
            <CardHeader className="bg-white border-0 pt-3 px-3 pb-2">
                <div className="d-flex align-items-center gap-2">
                    <span
                        className="d-inline-flex align-items-center justify-content-center"
                        style={{
                            width: 32, height: 32, borderRadius: 8,
                            background: "#daf4f0", color: "#0ab39c", flexShrink: 0,
                        }}
                        aria-hidden="true"
                    >
                        <i className="ri-building-line" style={{ fontSize: 16 }} />
                    </span>
                    <div>
                        <h6 className="mb-0 fw-semibold">Manage departments</h6>
                        <p className="text-muted small mb-0">Add or remove company departments</p>
                    </div>
                </div>
            </CardHeader>

            <CardBody className="pt-3">
                {/* ── Add department ── */}
                <div className="text-uppercase fw-semibold text-muted mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.04em' }}>
                    Add department
                </div>
                <Row className="g-2 align-items-center mb-3">
                    <Col xs="auto" style={{ minWidth: 120 }}>
                        <Input
                            bsSize="sm"
                            placeholder="Dept ID"
                            value={newDept.jcDept1}
                            onChange={e => setNewDept(p => ({ ...p, jcDept1: e.target.value }))}
                        />
                    </Col>
                    <Col xs="auto" style={{ minWidth: 220 }}>
                        <Input
                            bsSize="sm"
                            placeholder="Description"
                            value={newDept.description}
                            onChange={e => setNewDept(p => ({ ...p, description: e.target.value }))}
                        />
                    </Col>
                    <Col xs="auto">
                        <Button color="primary" size="sm" onClick={handleAdd}>
                            <i className="ri-add-line align-bottom me-1" />
                            Add department
                        </Button>
                    </Col>
                </Row>

                {/* ── Delete department ── */}
                <div className="text-uppercase fw-semibold text-muted mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.04em' }}>
                    Delete department
                </div>
                <Row className="g-2 align-items-center mb-3">
                    <Col xs="auto" style={{ minWidth: 320 }}>
                        <Input
                            type="select"
                            bsSize="sm"
                            value={selectedDeptId}
                            onChange={e => setSelectedDeptId(e.target.value)}
                        >
                            <option value="">Select department to delete</option>
                            {departments.map(d => (
                                <option key={d.jcDept1} value={d.jcDept1}>
                                    {d.jcDept1} — {d.description}
                                </option>
                            ))}
                        </Input>
                    </Col>
                    <Col xs="auto">
                        <Button color="danger" size="sm" onClick={handleDelete}>
                            <i className="ri-delete-bin-line align-bottom me-1" />
                            Delete
                        </Button>
                    </Col>
                </Row>

                {/* ── All departments table ── */}
                <div className="text-uppercase fw-semibold text-muted mb-2"
                    style={{ fontSize: 11, letterSpacing: '0.04em' }}>
                    All departments
                </div>
                <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0" hover>
                        <thead className="table-light">
                            <tr>
                                <th>ID</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            {departments.length > 0 ? departments.map((d) => (
                                <tr key={d.jcDept1}>
                                    <td>{d.jcDept1}</td>
                                    <td>{d.description}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={2} className="text-center text-muted py-3">
                                        No departments found.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                </div>
            </CardBody>
        </Card>
    );
};

export default ManageDepartments;
